import {
    Box,
    VStack,
    FormControl,
    FormLabel,
    Input,
    Button,
    HStack,
    NumberInput,
    NumberInputField,
    Checkbox,
    Stack,
    Text,
    Flex,
    Divider,
} from '@chakra-ui/react';
import { useAgencies } from '@/services/hooks';

export interface FilterState {
    agencyIds: number[];
    minPrice: number | undefined;
    maxPrice: number | undefined;
    dateFrom: string | undefined;
    dateTo: string | undefined;
}

interface ImportedItemsFiltersProps {
    filters: FilterState;
    onChange: (filters: FilterState) => void;
    onReset: () => void;
}

export function ImportedItemsFilters({ filters, onChange, onReset }: ImportedItemsFiltersProps) {
    const { data: agencies } = useAgencies();

    // Local state for immediate feedback, push to parent on change
    const handleChange = (key: keyof FilterState, value: any) => {
        onChange({ ...filters, [key]: value });
    };

    const handleAgencyToggle = (id: number) => {
        const current = filters.agencyIds;
        const next = current.includes(id)
            ? current.filter(aid => aid !== id)
            : [...current, id];
        handleChange('agencyIds', next);
    };

    return (
        <Flex direction="column" h="100%">
            <VStack spacing={6} align="stretch" flex="1" overflowY="auto" px={1}>
                {/* Agency Filter */}
                <FormControl>
                    <FormLabel fontSize="sm" fontWeight="bold" mb={3}>Agencies</FormLabel>
                    <Stack pl={1} spacing={2} maxH="300px" overflowY="auto">
                        {agencies?.map((agency: any) => (
                            <Checkbox
                                key={agency.id}
                                size="md"
                                colorScheme="brand"
                                isChecked={filters.agencyIds.includes(agency.id)}
                                onChange={() => handleAgencyToggle(agency.id)}
                            >
                                <Text fontSize="sm">{agency.name}</Text>
                            </Checkbox>
                        ))}
                    </Stack>
                </FormControl>

                <Divider />

                {/* Price Range */}
                <FormControl>
                    <FormLabel fontSize="sm" fontWeight="bold" mb={3}>Unit Price ($)</FormLabel>
                    <HStack>
                        <NumberInput size="sm" min={0} value={filters.minPrice || ''} onChange={(_, val) => handleChange('minPrice', val || undefined)}>
                            <NumberInputField placeholder="Min" borderRadius="md" />
                        </NumberInput>
                        <Text color="gray.500">-</Text>
                        <NumberInput size="sm" min={0} value={filters.maxPrice || ''} onChange={(_, val) => handleChange('maxPrice', val || undefined)}>
                            <NumberInputField placeholder="Max" borderRadius="md" />
                        </NumberInput>
                    </HStack>
                </FormControl>

                <Divider />

                {/* Date Range */}
                <FormControl>
                    <FormLabel fontSize="sm" fontWeight="bold" mb={3}>Date Range</FormLabel>
                    <VStack spacing={3}>
                        <Box w="full">
                            <Text fontSize="xs" color="gray.500" mb={1}>From</Text>
                            <Input
                                type="date"
                                size="sm"
                                borderRadius="md"
                                value={filters.dateFrom || ''}
                                onChange={(e) => handleChange('dateFrom', e.target.value || undefined)}
                            />
                        </Box>
                        <Box w="full">
                            <Text fontSize="xs" color="gray.500" mb={1}>To</Text>
                            <Input
                                type="date"
                                size="sm"
                                borderRadius="md"
                                value={filters.dateTo || ''}
                                onChange={(e) => handleChange('dateTo', e.target.value || undefined)}
                            />
                        </Box>
                    </VStack>
                </FormControl>
            </VStack>

            <Box pt={4} mt={4} borderTopWidth="1px">
                <Button w="full" onClick={onReset} variant="outline" size="sm">
                    Clear All Filters
                </Button>
            </Box>
        </Flex>
    );
}
